import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  listBookings, respondToBooking, BOOKING_LABEL,
  type VenueBooking, type BookingStatus,
} from '../../lib/venue';
import { ApiError } from '../../lib/apiClient';
import { formatDate, formatTime } from '../../lib/format';
import './VenuePages.css';

/**
 * Booking requests, grouped by where they stand.
 *
 * The state machine, verified against the engine rather than assumed from the
 * task that built it:
 *
 *   NULL --organizer requests--> pending --venue responds--> confirmed
 *                                   |                     \-> declined
 *   declined --organizer requests again--> pending
 *
 * 'confirmed' is the only terminal state. A declined request CAN be re-sent by
 * the organizer, so the decline confirmation says that plainly instead of
 * calling it final — and confirming is the irreversible one, which is where the
 * warning belongs.
 *
 * Confirming also writes a hold onto the availability calendar for the event
 * date. That is the engine's behaviour, so the prompt states it.
 */

const GROUPS: { status: BookingStatus; note: string }[] = [
  { status: 'pending', note: 'Sent by an organizer, waiting on you.' },
  { status: 'confirmed', note: 'You have committed the room. The date is held on your calendar.' },
  { status: 'declined', note: 'You turned these down. An organizer can send the request again.' },
];

function BookingCard({ b, onChanged }: { b: VenueBooking; onChanged: () => void }) {
  const [confirming, setConfirming] = useState<'confirm' | 'decline' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function respond(action: 'confirm' | 'decline') {
    setBusy(true);
    setError(null);
    try {
      await respondToBooking(b.eventId, action);
      setConfirming(null);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send your response.');
      setBusy(false);
    }
  }

  const pending = b.bookingStatus === 'pending';

  return (
    <article className={[
      'ven-card',
      pending ? 'ven-card--action' : '',
      b.bookingStatus === 'declined' ? 'ven-card--muted' : '',
    ].filter(Boolean).join(' ')}>
      <div className="ven-row">
        <div className="ven-row__main">
          <span className="ven-row__title">{b.title}</span>
          <span className="ven-row__meta">
            {b.eventDate ? formatDate(b.eventDate) : 'Date TBC'}
            {b.eventTime ? ` · ${formatTime(b.eventTime)}` : ''}
            {b.doorsTime ? ` · doors ${formatTime(b.doorsTime)}` : ''}
          </span>
          <span className="ven-hint">
            Organizer: {b.organizer || '-'} · event is {b.eventStatus}
          </span>
        </div>
        <div className="ven-row__side">
          {b.expectedAttendance != null && (
            <span className="ven-hint mono">{b.expectedAttendance} expected</span>
          )}
          <span className={`ven-badge ven-badge--${b.bookingStatus}`}>
            {BOOKING_LABEL[b.bookingStatus]}
          </span>
        </div>
      </div>

      {error && <p className="ven-error" role="alert">{error}</p>}

      {confirming === 'confirm' && (
        <div className="ven-prompt">
          <h4 className="ven-prompt__title">Confirm this booking?</h4>
          <div className="ven-prompt__body">
            <ul className="ven-consequences">
              <li>You commit the room for <strong>{formatDate(b.eventDate)}</strong>.</li>
              <li>That date is <strong>held on your calendar</strong> and can no longer be
                edited there.</li>
              <li>This cannot be undone from here, the organizer would have to cancel.</li>
            </ul>
          </div>
          <div className="ven-prompt__actions">
            <button type="button" className="button button--ghost" disabled={busy}
              onClick={() => setConfirming(null)}>Cancel</button>
            <button type="button" className="button button--primary" disabled={busy}
              onClick={() => respond('confirm')}>
              {busy ? 'Confirming…' : 'Confirm booking'}
            </button>
          </div>
        </div>
      )}

      {confirming === 'decline' && (
        <div className="ven-prompt">
          <h4 className="ven-prompt__title">Decline this request?</h4>
          <div className="ven-prompt__body">
            <p>
              The date is <strong>not</strong> held, and your calendar is unchanged.
            </p>
            <p>
              Declining is not final: the organizer can send the same request again if
              things change.
            </p>
          </div>
          <div className="ven-prompt__actions">
            <button type="button" className="button button--ghost" disabled={busy}
              onClick={() => setConfirming(null)}>Keep it</button>
            <button type="button" className="button button--ghost ven-danger" disabled={busy}
              onClick={() => respond('decline')}>
              {busy ? 'Declining…' : 'Decline request'}
            </button>
          </div>
        </div>
      )}

      {!confirming && (
        <div className="ven-actions">
          {pending && (
            <>
              <button type="button" className="button button--primary"
                onClick={() => setConfirming('confirm')}>Confirm</button>
              <button type="button" className="button button--ghost ven-danger"
                onClick={() => setConfirming('decline')}>Decline</button>
            </>
          )}
          {b.bookingStatus === 'confirmed' && (
            <Link className="button button--secondary" to={`/venue/events/${b.eventId}`}>
              Event day
            </Link>
          )}
        </div>
      )}
    </article>
  );
}

export function VenueBookings() {
  const [rows, setRows] = useState<VenueBooking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    listBookings()
      .then((r) => { setRows(r.bookings); setError(null); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError
          ? (err.status === 404 ? 'Create your venue profile first.' : err.message)
          : 'Could not load your bookings.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  if (loading) return <p className="ven-status">Loading…</p>;
  if (error) return <p className="ven-status ven-error" role="alert">{error}</p>;

  return (
    <div className="ven-page">
      <header className="ven-header">
        <div>
          <h1 className="ven-title">Bookings</h1>
          <p className="ven-lede">
            Every event an organizer has pointed at this venue.
          </p>
        </div>
      </header>

      {rows.length === 0 && (
        <p className="ven-status ven-muted">
          No booking requests yet. Organizers reach you by attaching their event to your
          venue.
        </p>
      )}

      {GROUPS.map(({ status, note }) => {
        const group = rows.filter((b) => b.bookingStatus === status);
        if (group.length === 0) return null;
        return (
          <section key={status} className="ven-section">
            <div className="ven-section__head">
              <h2 className="ven-section__title">
                {BOOKING_LABEL[status]} <span className="ven-hint mono">({group.length})</span>
              </h2>
            </div>
            <p className="ven-section__note">{note}</p>
            <div className="ven-list">
              {group.map((b) => <BookingCard key={b.eventId} b={b} onChanged={load} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}
